(() => {
  if (window.customElements.get('product-story-explainer')) return;

  class ProductStoryExplainer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.setupReveals();
      this.setupParallax();
      this.setupAccordions();
    }

    disconnectedCallback() {
      this.revealObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    setupReveals() {
      const elements = [...this.querySelectorAll('[data-story-reveal]')];
      if (this.reducedMotion || !('IntersectionObserver' in window)) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
      }
      this.revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -9% 0px' });
      elements.forEach((element) => this.revealObserver.observe(element));
    }

    setupParallax() {
      const target = this.querySelector('[data-story-parallax]');
      const parent = target?.parentElement;
      if (!target || !parent || this.reducedMotion) return;
      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = parent.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        if (rect.bottom < 0 || rect.top > viewport) return;
        const progress = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - viewport / 2) / viewport));
        target.style.setProperty('--story-parallax-y', `${progress * -26}px`);
      };
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        this.frame = requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      update();
    }

    setupAccordions() {
      this.querySelectorAll('[data-story-accordion-button]').forEach((button) => {
        const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        if (!panel) return;
        button.addEventListener('click', () => {
          const isOpen = button.getAttribute('aria-expanded') === 'true';
          isOpen ? this.closePanel(button, panel) : this.openPanel(button, panel);
        });
      });
    }

    openPanel(button, panel) {
      button.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      if (this.reducedMotion) return;
      const height = panel.scrollHeight;
      panel.animate([{ height: '0px', opacity: 0 }, { height: `${height}px`, opacity: 1 }], {
        duration: 320,
        easing: 'cubic-bezier(.16,1,.3,1)'
      }).onfinish = () => { panel.style.height = 'auto'; };
    }

    closePanel(button, panel) {
      button.setAttribute('aria-expanded', 'false');
      if (this.reducedMotion) {
        panel.hidden = true;
        return;
      }
      const height = panel.getBoundingClientRect().height;
      panel.animate([{ height: `${height}px`, opacity: 1 }, { height: '0px', opacity: 0 }], {
        duration: 240,
        easing: 'cubic-bezier(.4,0,1,1)'
      }).onfinish = () => {
        panel.hidden = true;
        panel.style.height = '';
      };
    }
  }

  window.customElements.define('product-story-explainer', ProductStoryExplainer);
})();
